import React, { useCallback, useEffect, useState } from 'react'

import { ButtonContainer } from 'styles/shared'
import styled from 'styled-components'
import { color } from 'styles/colors'
import ArrowIcon from '@mui/icons-material/TrendingFlat'

import Modal from 'components/Modal'
import ModalButton from 'components/Modal/Button'
import Spinner from 'components/Spinner'

import Apptentive from 'helpers/apptentive'
import GA4 from 'helpers/ga4'
import { shortDate } from 'helpers/formatters'

import Api from 'services/api'

import { PaywallPlanChange } from 'types/PaywallPlanChange'

const NO_PLAN_CHANGE = 'No more changes can be made at this time.'

type Props = {
  closeCallback: (reload: boolean) => void
  dealerId: number
  planChange?: PaywallPlanChange
  effectiveDate?: string
  inTrial?: boolean
}

const CancelModal = ({ dealerId, planChange, closeCallback, effectiveDate, inTrial } : Props) => {
  const [isSaving, setIsSaving] = useState(false)
  const [planIsAlreadyChanging, setPlanIsAlreadyChanging] = useState(false)
  const [cancellingPlan, setCancellingPlan] = useState<string>(`If you cancel your plan, you will lose access to the premium features and your account will be deactivated on ${shortDate(effectiveDate)}.`)

  useEffect(() => {
    if (planChange?.effectiveDate && !inTrial) {
      setPlanIsAlreadyChanging(true)
    }

    if (inTrial) {
      setCancellingPlan('If you cancel, you will lose access to the premium features immediately.  You will continue to have access to the standard diagnostics features.')
    }
  }, [planChange, inTrial])

  const changeInProgress = `A change to the plan has already been initiated and will take effect on ${shortDate(planChange?.effectiveDate)}. Please contact support 844-359-0270.`

  const onClickCancel = useCallback(async () => {
    const planType = planChange?.currentPlanId === 'diag_monthly' ? 'Monthly' : 'Yearly'

    GA4.sendEvent({
      action: 'Click Cancel Plan Renewal',
      category: 'Premium Settings',
      label: `Plan Type: ${planType}`
    })

    setIsSaving(true)
    await Api.Paywall.cancelPlan(dealerId, 'premium')
      .then(() => {
        Apptentive.updatePerson({ tier: 'standard', plan: null })

        if (inTrial) {
          Apptentive.sendEvent('premium_trial_canceled')
          GA4.sendEvent({ category: 'Premium', action: 'Premium Trial Canceled' })
        } else {
          Apptentive.sendEvent('premium_canceled')
          GA4.sendEvent({ category: 'Premium', action: 'Premium Canceled' })
        }

        closeCallback(true)
      }).catch(() => setIsSaving(false))
  }, [closeCallback, dealerId, planChange, inTrial])

  const onClickClose = () => {
    closeCallback(false)
  }

  return (
    <Modal variant="sm" title="Cancel Plan" isDisplayed closeCallback={() => closeCallback(false)} >
      <S.Top />
      <S.Form>
        {planIsAlreadyChanging &&
          <>
            <S.Explanation>{NO_PLAN_CHANGE}</S.Explanation>
            <S.Explanation>{changeInProgress}</S.Explanation>
          </>
        }
        {!planIsAlreadyChanging && <S.Explanation>{cancellingPlan}</S.Explanation> }
      </S.Form>
      <S.ButtonContainer>
        {!planIsAlreadyChanging &&
          <ModalButton
            decoration="secondary"
            text="Cancel Plan"
            onPress={onClickCancel}
            showSpinner={isSaving}
            styles={{ button: { height: 32, borderRadius: 10, width: 120 } }}
          />
        }
        <ModalButton
          decoration="positive"
          text="Close"
          onPress={onClickClose}
          styles={{ button: { height: 32, borderRadius: 10, width: 120 } }}
        />
      </S.ButtonContainer>
    </Modal>
  )
}

export default CancelModal

const S = {
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    color: ${color('breadCrumbsTextHover')};
  `,
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    max-width: 800px;
    margin-top: 0px;
  `,
  Explanation: styled.div`
    display: flex;
    margin-bottom: 20px;
    text-align: left;
    color: ${color('premiumText')};
    white-space: pre-line;
  `,
  InnerContainer: styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${color('titleBackground')};
    padding: 10px 15px 15px 15px;
  `,
  Form: styled.form`
    display: flex;
    grid-column-gap: 50px;
    flex-wrap: wrap;
    justify-content: space-between;
  `,
  Spinner: styled(Spinner)`
    margin: 60px 0 0 0;
    width: 32px;
    height: 32px;
    border-radius: 18px;
  `,
  Top: styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
  `
}
